import {
  LocalStorageStickyBucketService,
  GrowthBook as NativeGrowthBook,
  TrackingCallback,
} from '@growthbook/growthbook';

import {
  FeatureVariant,
  GB_FEATURES,
  GBFeatureKey,
} from '~/services/abTests/features';

class GrowthBook {
  instance: null | NativeGrowthBook = null;

  setUp(
    clientKey: string,
    trackingCallbacks: TrackingCallback[],
    attributes?: Record<string, string>,
  ): void {
    if (clientKey) {
      this.instance = new NativeGrowthBook({
        apiHost: 'https://growthbook.justdone.ai',
        clientKey,
        enableDevMode: true,
        attributes,
        trackingCallback: (experiment, result): void => {
          trackingCallbacks.forEach((cb) => cb(experiment, result));
        },
        stickyBucketService: new LocalStorageStickyBucketService(),
      });
    }
  }

  async init(attributes: Record<string, any>): Promise<void> {
    if (this.instance) {
      await this.instance.setAttributes(attributes);
      await this.instance.init();
    }
  }

  getFeatureValue<K extends GBFeatureKey>(key: K): FeatureVariant<K> {
    const defaultValue = GB_FEATURES[key].defaultVariant;

    if (!this.instance) {
      return defaultValue;
    }

    return this.instance.getFeatureValue(
      key,
      defaultValue,
    ) as FeatureVariant<K>;
  }

  isTestGroup<K extends GBFeatureKey>(key: K): boolean {
    if (!this.instance) {
      return false;
    }

    const defaultValue = GB_FEATURES[key].defaultVariant;
    const control = GB_FEATURES[key].variants.control;

    return this.instance.getFeatureValue(key, defaultValue) !== control;
  }

  compareAbTests<K extends GBFeatureKey>(
    items: { key: K; value: FeatureVariant<K> }[],
  ): boolean {
    return items.every(({ key, value }) => this.getFeatureValue(key) === value);
  }

  startAbTests(): void {
    if (!this.instance) return;

    (Object.keys(GB_FEATURES) as GBFeatureKey[]).forEach((key) => {
      const feature = GB_FEATURES[key];
      this.instance?.getFeatureValue(key, feature.defaultVariant);
    });
  }
}

const growthBook = new GrowthBook();

export { growthBook };
